import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Calendar,
  Users,
  UserCog,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  Euro,
  FileQuestion,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import navlogo from "../../assets/navlogo.png";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem("adminToken");
    const expiry = localStorage.getItem("adminTokenExpiry");
    
    // Check if token exists and hasn't expired
    if (!token || (expiry && Date.now() > parseInt(expiry))) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminTokenExpiry");
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/me`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setAdminUser(userData);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Token verification failed:", error);
      return false;
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (location.pathname === "/admin/login") {
        setIsAuthenticated(false);
        return;
      }

      const isValid = await verifyToken();
      if (!isValid) {
        toast.error("Session expired. Please login again.");
        navigate("/admin/login");
      } else {
        setIsAuthenticated(true);
      }
    };

    checkAuth();
  }, [location.pathname, navigate, verifyToken]);

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: Calendar, label: "Bookings", path: "/admin/bookings" },
    { icon: FileQuestion, label: "Quotes", path: "/admin/quotes" },
    { icon: Euro, label: "Rates", path: "/admin/rates" },
    { icon: Users, label: "Customers", path: "/admin/customers" },
    { icon: UserCog, label: "Users", path: "/admin/users" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpiry");
    setIsAuthenticated(false);
    setAdminUser(null);
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  if (location.pathname === "/admin/login") {
    return <Outlet />;
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Verifying authentication...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full w-64 bg-slate-900 transform transition-transform duration-300 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <img
              src={navlogo}
              alt="Best Disney Transfer"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        <nav className="px-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                location.pathname === item.path
                  ? "bg-primary-600 text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="flex items-center gap-4">
              {/* <button className="p-2 rounded-lg hover:bg-slate-100 relative">
                <Bell className="w-5 h-5 text-slate-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button> */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                  A
                </div>
                <span className="text-sm font-medium text-slate-700 hidden sm:block">Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
